import { createTheme } from '@mui/material/styles';

const darkTheme = createTheme({
    palette: {
        mode: 'dark',
        primary: {
            main: 'rgb(76, 29, 149)',
            light: 'rgb(91, 33, 182)',
            dark: 'rgb(59, 7, 100)',
            contrastText: '#ffffff',
        },
        secondary: {
            main: 'rgb(239, 68, 68)',
            light: 'rgb(248, 113, 113)',
            dark: 'rgb(185, 28, 28)',
            contrastText: '#ffffff',
        },
        success: {
            main: 'rgb(34, 197, 94)',
            light: 'rgb(74, 222, 128)',
            dark: 'rgb(22, 163, 74)',
        },
        warning: {
            main: 'rgb(245, 158, 11)',
            light: 'rgb(251, 191, 36)',
            dark: 'rgb(217, 119, 6)',
        },
        background: {
            default: '#0f172a',
            paper: 'rgba(17, 24, 39, 0.95)',
        },
        text: {
            primary: '#ffffff',
            secondary: 'rgb(196, 181, 253)',
            disabled: 'rgb(156, 163, 175)',
        },
        divider: 'rgb(55, 65, 81)',
        action: {
            hover: 'rgba(76, 29, 149, 0.08)',
            selected: 'rgba(76, 29, 149, 0.16)',
            disabled: 'rgba(156, 163, 175, 0.3)',
        },
    },
    typography: {
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
        h1: {
            fontSize: '2rem',
            fontWeight: 700,
            lineHeight: 1.2,
        },
        h2: {
            fontSize: '1.75rem',
            fontWeight: 600,
            lineHeight: 1.3,
        },
        h3: {
            fontSize: '1.5rem',
            fontWeight: 600,
            lineHeight: 1.4,
        },
        h4: {
            fontSize: '1.25rem',
            fontWeight: 600,
            lineHeight: 1.4,
        },
        h5: {
            fontSize: '1.125rem',
            fontWeight: 600,
            lineHeight: 1.4,
        },
        h6: {
            fontSize: '1rem',
            fontWeight: 600,
            lineHeight: 1.4,
        },
        body1: {
            fontSize: '0.875rem',
            lineHeight: 1.5,
        },
        body2: {
            fontSize: '0.75rem',
            lineHeight: 1.5,
        },
        button: {
            fontSize: '0.875rem',
            fontWeight: 500,
            textTransform: 'none',
        },
        caption: {
            fontSize: '0.75rem',
            lineHeight: 1.4,
        },
    },
    shape: {
        borderRadius: 8,
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 6,
                    textTransform: 'none',
                    fontWeight: 500,
                    boxShadow: 'none',
                    color: 'rgba(241, 245, 249, 0.98)',
                    '&:hover': {
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                    },
                },
                contained: {
                    color: '#ffffff',
                },
                containedPrimary: {
                    backgroundColor: '#5b21b6',
                    color: '#ffffff',
                    '&:hover': {
                        backgroundColor: '#6d28d9',
                    },
                },
                outlined: {
                    color: 'rgba(226, 232, 240, 0.96)',
                    borderColor: 'rgba(167, 139, 250, 0.5)',
                    backgroundColor: 'rgba(76, 29, 149, 0.08)',
                    '&:hover': {
                        borderColor: 'rgba(167, 139, 250, 0.82)',
                        backgroundColor: 'rgba(124, 58, 237, 0.16)',
                    },
                },
                text: {
                    color: 'rgba(226, 232, 240, 0.94)',
                    '&:hover': {
                        backgroundColor: 'rgba(124, 58, 237, 0.14)',
                    },
                },
                sizeSmall: {
                    padding: '6px 12px',
                    fontSize: '0.75rem',
                },
                sizeMedium: {
                    padding: '8px 16px',
                    fontSize: '0.875rem',
                },
                sizeLarge: {
                    padding: '10px 20px',
                    fontSize: '1rem',
                },
                disabled: {
                    color: 'rgba(148, 163, 184, 0.55)',
                    borderColor: 'rgba(148, 163, 184, 0.25)',
                },
            },
        },
        MuiToggleButton: {
            styleOverrides: {
                root: {
                    color: 'rgba(226, 232, 240, 0.94)',
                    borderColor: 'rgba(167, 139, 250, 0.42)',
                    '&:hover': {
                        backgroundColor: 'rgba(124, 58, 237, 0.14)',
                    },
                    '&.Mui-selected': {
                        color: '#ffffff',
                        backgroundColor: '#5b21b6',
                        borderColor: 'rgba(167, 139, 250, 0.88)',
                        '&:hover': {
                            backgroundColor: '#6d28d9',
                        },
                    },
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        borderRadius: 6,
                    },
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    backgroundImage: 'none',
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    backgroundImage: 'none',
                },
            },
        },
        MuiTable: {
            styleOverrides: {
                root: {
                    '& .MuiTableCell-root': {
                        borderBottom: '1px solid rgb(55, 65, 81)',
                    },
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    padding: '8px 12px',
                },
                head: {
                    fontWeight: 600,
                    backgroundColor: 'rgba(76, 29, 149, 0.1)',
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: 4,
                    fontWeight: 500,
                },
                sizeSmall: {
                    height: 20,
                    fontSize: '0.65rem',
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    borderRadius: 6,
                },
                sizeSmall: {
                    padding: 4,
                },
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    borderRadius: 12,
                    backgroundImage: 'none',
                },
            },
        },
        MuiTabs: {
            styleOverrides: {
                root: {
                    '& .MuiTab-root': {
                        textTransform: 'none',
                        fontWeight: 500,
                        minHeight: 48,
                    },
                },
            },
        },
    },
});

export default darkTheme;
